// src/pages/AdminPage.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import AdminBlog from '../components/admin/AdminBlog';
import AdminClients from '../components/admin/AdminClients';
import AdminMail from '../components/admin/mail/AdminMail';
import { Menu, X, FileText, Users, Mail } from 'lucide-react';

const AdminContainer = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    display: flex;
`;

const Sidebar = styled(motion.aside)<{ $collapsed: boolean }>`
    width: ${p => p.$collapsed ? '80px' : '280px'};
    background: linear-gradient(135deg, #1e1b2e 0%, #2d1b3d 100%);
    color: white;
    padding: 2rem 0;
    box-shadow: 8px 0 30px rgba(0, 0, 0, 0.2);
    position: relative;
    transition: width 0.4s ease;
    z-index: 10;

    @media (max-width: 768px) {
        position: fixed;
        left: 0;
        top: 0;
        height: 100vh;
        z-index: 50;
    }
`;

const SidebarHeader = styled.div<{ $collapsed: boolean }>`
    padding: ${p => p.$collapsed ? '0 0 2rem' : '0 1.5rem 2rem'};
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: ${p => p.$collapsed ? 'center' : 'space-between'};
    transition: padding 0.4s ease;
`;

const Logo = styled.div<{ $collapsed: boolean }>`
    font-size: 1.6rem;
    font-weight: 800;
    background: linear-gradient(135deg, #990147, #cc3366);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    opacity: ${p => p.$collapsed ? 0 : 1};
    /* Zero-width as well as transparent. Fading alone leaves it occupying its full width,
       which in an 80px sidebar pushes the toggle button clean off the edge — leaving no way
       to expand the menu again. */
    width: ${p => p.$collapsed ? '0' : 'auto'};
    overflow: hidden;
    white-space: nowrap;
    transition: opacity 0.3s ease, width 0.3s ease;
`;

const ToggleButton = styled.button`
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 8px;
    &:hover { background: rgba(255,255,255,0.1); }
`;

const NavList = styled.ul`
    list-style: none;
    padding: 1rem 0;
    margin: 0;
`;

const NavItem = styled(motion.li)<{ $active: boolean; $collapsed: boolean }>`
    margin: 0.5rem ${p => p.$collapsed ? '0.5rem' : '1rem'};
    border-radius: 12px;
    background: ${p => p.$active ? 'rgba(153, 1, 71, 0.3)' : 'transparent'};
    border-left: 4px solid ${p => p.$active ? '#990147' : 'transparent'};
    overflow: hidden;
    transition: margin 0.4s ease;
`;

const NavLink = styled(motion.button)<{ $collapsed: boolean }>`
    width: 100%;
    padding: ${p => p.$collapsed ? '1.1rem 0' : '1.1rem 1.5rem'};
    background: none;
    border: none;
    color: white;
    text-align: left;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
    gap: ${p => p.$collapsed ? '0' : '1rem'};
    font-size: 1rem;
    font-weight: 500;
    transition: all 0.3s ease;
    box-sizing: border-box;

    /* The label has to collapse as well as fade, otherwise it keeps its width inside an
       80px sidebar and NavItem's overflow:hidden clips it into a half-visible pill.
       The fade transition was already here; nothing had ever driven it. */
    span:last-child {
        transition: opacity 0.3s ease, width 0.3s ease;
        opacity: ${p => p.$collapsed ? 0 : 1};
        width: ${p => p.$collapsed ? '0' : 'auto'};
        overflow: hidden;
        white-space: nowrap;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const MainContent = styled.main`
    margin-top: 100px;
    flex: 1;
    padding: 3rem;
    overflow-y: auto;

    @media (max-width: 768px) {
        padding: 2rem 1rem;
    }
`;

const Header = styled.div`
    margin-bottom: 3rem;
`;

const Title = styled(motion.h1)`
    font-size: 2.8rem;
    font-weight: 800;
    background: linear-gradient(135deg, #333333, #990147);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
    color: #64748b;
    font-size: 1.1rem;
`;

const ContentWrapper = styled(motion.div)`
    background: white;
    border-radius: 20px;
    padding: 2.5rem;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(153, 1, 71, 0.1);
    min-height: 70vh;
`;

type AdminSection = 'blog' | 'clients' | 'mail';

const AdminPage: React.FC = () => {
    const [activeSection, setActiveSection] = useState<AdminSection>('blog');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const navItems = [
        { id: 'blog' as const, label: 'Blog', icon: FileText },
        { id: 'clients' as const, label: 'Klienti', icon: Users },
        { id: 'mail' as const, label: 'Pošta', icon: Mail },
    ];

    // An exhaustive map, not a ternary. With three sections a two-way `? :` would silently
    // render the clients table for 'mail', and TypeScript would not catch it.
    const sections: Record<AdminSection, React.ReactNode> = {
        blog: <AdminBlog />,
        clients: <AdminClients />,
        mail: <AdminMail />,
    };

    return (
        <AdminContainer>
            <Sidebar
                $collapsed={sidebarCollapsed}
                initial={{ x: -300 }}
                animate={{ x: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
            >
                <SidebarHeader $collapsed={sidebarCollapsed}>
                    <Logo $collapsed={sidebarCollapsed}>Admin Panel</Logo>
                    <ToggleButton
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        title={sidebarCollapsed ? 'Rozbaliť menu' : 'Zbaliť menu'}
                        aria-label={sidebarCollapsed ? 'Rozbaliť menu' : 'Zbaliť menu'}
                    >
                        {sidebarCollapsed ? <Menu size={24} /> : <X size={24} />}
                    </ToggleButton>
                </SidebarHeader>

                <NavList>
                    {navItems.map((item) => (
                        <NavItem
                            key={item.id}
                            $active={activeSection === item.id}
                            $collapsed={sidebarCollapsed}
                            whileHover={{ x: 5 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <NavLink
                                $collapsed={sidebarCollapsed}
                                title={sidebarCollapsed ? item.label : undefined}
                                onClick={() => setActiveSection(item.id)}
                            >
                                <item.icon size={22} />
                                <span>{item.label}</span>
                            </NavLink>
                        </NavItem>
                    ))}
                </NavList>
            </Sidebar>

            <MainContent>
                <Header>

                </Header>

                <AnimatePresence mode="wait">
                    <ContentWrapper
                        key={activeSection}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                    >
                        {sections[activeSection]}
                    </ContentWrapper>
                </AnimatePresence>
            </MainContent>
        </AdminContainer>
    );
};

export default AdminPage;